import type {
  ConfirmOptions,
  EventTopic,
  ModuleContext,
  ModuleSidebar,
  Unsubscribe,
} from '@one/module-sdk';
import { authConfigured, loginRequest, msalInstance } from '../auth';
import type { Bootstrap, ManifestEntry, TenantInfo } from './types';

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

/**
 * Die API hat die Anfrage endgültig abgelehnt. Ein Wiederholen hilft nicht — im
 * Gegensatz zu einem Netzwerkfehler oder einem Kaltstart der API.
 */
export class ApiAbgelehnt extends Error {}

/**
 * Fetch mit Bearer-Token (falls Entra konfiguriert) — auch für Shell-eigene Aufrufe.
 *
 * Ein 401 löst hier bewusst **keine** neue Anmeldung aus. Genau das tat die erste
 * Fassung — und weil die API jedes gültige Token wegen einer falschen Audience mit
 * 401 abwies, entstand eine Anmeldeschleife: Token holen → 401 → Redirect → Token
 * holen … Microsoft hat sie nach sechs Runden mit „Eine Anmeldung ist nicht möglich"
 * abgebrochen, was wie ein Problem bei Microsoft aussah. Ein 401 auf eine Anfrage
 * **mit** Token ist eine Ablehnung durch den Server; eine erneute Anmeldung kann
 * daran nichts ändern. Ein abgelaufenes Token erneuert `acquireTokenSilent` ohnehin.
 */
export async function apiFetch(
  path: string,
  init?: RequestInit,
  mandant?: string,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  let mitToken = false;

  // Welcher Mandant angezeigt wird, sagt die Shell — ob er erlaubt ist, entscheidet
  // die API (Kap. 04 §3, Regel 1). Der Header ist deshalb ein Wunsch, kein Recht.
  if (mandant) headers.set('X-One-Mandant', mandant);

  if (authConfigured && msalInstance) {
    const account = msalInstance.getAllAccounts()[0];
    if (account) {
      try {
        const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        headers.set('Authorization', `Bearer ${result.accessToken}`);
        mitToken = true;
      } catch {
        // Stille Erneuerung nicht möglich (Sitzung bei Microsoft abgelaufen) — hier
        // ist eine neue Anmeldung die richtige und einzige Antwort.
        await msalInstance.acquireTokenRedirect(loginRequest);
        throw new Error('Anmeldung abgelaufen — sie wird erneuert.');
      }
    }
  }

  const response = await fetch(`${API_BASE}/${path.replace(/^\//, '')}`, { ...init, headers });

  if (response.status === 401 && mitToken) {
    throw new ApiAbgelehnt(
      'Die Plattform hat die Anmeldung abgelehnt (401 trotz gültiger Anmeldung). ' +
        'Das ist ein Konfigurationsfehler auf der Serverseite, keine abgelaufene Sitzung.',
    );
  }
  return response;
}

export interface ShellServices {
  bootstrap: Bootstrap;
  activeTenant: TenantInfo;
  navigate: (path: string) => void;
  toast: (message: string, kind?: 'info' | 'success' | 'error') => void;
  /**
   * Rückfrage als Dialog der Shell (Warteschlange in `rueckfrage.ts`). Nicht `window.confirm`:
   * Eingebettete Browser und „weitere Dialoge verhindern“ unterdrücken es still → `false`.
   */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Aktuelles Theme und seine Änderungen (data-theme auf <html>). */
  theme: () => 'light' | 'dark';
  onThemeChange: (cb: (theme: 'light' | 'dark') => void) => Unsubscribe;
  /** Das Modul meldet seine Seiten (Contract 0.4). */
  setSidebar: (moduleId: string, sidebar: ModuleSidebar) => void;
  /** Handlungsbedarf eines Moduls → Punkt in der Rail. */
  setBadgeCount: (moduleId: string, count: number) => void;
}

/** Was die Shell neben dem Kontext braucht, um ein Modul zu steuern. */
export interface ModuleHandle {
  ctx: ModuleContext;
  /** Meldet einen Seitenwechsel an alle `ctx.navigation.onNavigate`-Listener. */
  notifyNavigate: (subpath: string) => void;
}

/**
 * `ctx.permissions.has()` für ein Modul — Komfort, nie Schutz: die API prüft
 * dieselbe Berechtigung noch einmal (Kap. 04 §4, „Frontend ist nur Komfort").
 * `*` steht ausschließlich für den Dev-Modus ohne Entra und ohne Datenbank.
 */
export function hatRecht(permissions: string[], recht: string): boolean {
  return permissions.includes(recht) || permissions.includes('*');
}

/** Baut den ModuleContext, den die Shell einem Modul beim Mount übergibt. */
export function buildModuleContext(
  entry: ManifestEntry,
  services: ShellServices,
  optionen: {
    /** Basis für `ctx.api.fetch`: Vorgabe `<modul>`; in der Modulvorschau (P11) `vorschau/<modul>`. */
    apiBasis?: string;
  } = {},
): ModuleHandle {
  const basis = optionen.apiBasis ?? entry.id;
  const events = new Map<string, Set<(payload: unknown) => void>>();
  const navListeners = new Set<(subpath: string) => void>();
  const { bootstrap, activeTenant } = services;

  const ctx: ModuleContext = {
    user: {
      id: bootstrap.user.id,
      displayName: bootstrap.user.displayName,
      email: bootstrap.user.email,
      entraObjectId: bootstrap.user.id,
    },
    tenant: { id: activeTenant.id, name: activeTenant.name, color: activeTenant.color },
    memberships: bootstrap.memberships,
    permissions: { has: (recht: string) => hatRecht(bootstrap.permissions, recht) },
    simulation: bootstrap.simulation ? { active: true, role: bootstrap.simulation.roleKey } : null,
    api: {
      fetch: (path, init) =>
        apiFetch(`${basis}/${path.replace(/^\//, '')}`, init, activeTenant.slug),
    },
    navigation: {
      basePath: entry.route,
      navigate: services.navigate,
      onNavigate(cb): Unsubscribe {
        navListeners.add(cb);
        return () => navListeners.delete(cb);
      },
      setSidebar: (sidebar) => services.setSidebar(entry.id, sidebar),
    },
    events: {
      emit(topic: EventTopic, payload: unknown) {
        if (topic === 'badge-count') {
          const count = Number((payload as { count?: unknown } | null)?.count ?? 0);
          services.setBadgeCount(entry.id, Number.isFinite(count) ? count : 0);
        }
        events.get(topic)?.forEach((cb) => cb(payload));
      },
      on(topic: EventTopic, cb): Unsubscribe {
        if (!events.has(topic)) events.set(topic, new Set());
        events.get(topic)!.add(cb);
        return () => events.get(topic)?.delete(cb);
      },
    },
    // Flags kommen vom Server und gelten je Mandant (Kap. 08). Was nicht
    // ausdrücklich an ist, ist aus — ein neues Feature bleibt unsichtbar, bis
    // jemand es einschaltet.
    featureFlags: { isEnabled: (key: string) => bootstrap.featureFlags?.[key] === true },
    ui: {
      theme: services.theme(),
      onThemeChange: services.onThemeChange,
      locale: 'de-DE',
      toast: services.toast,
      confirm: services.confirm,
      requestFourEyes: async (request) => {
        // Phase 1: echter Vier-Augen-Service folgt mit dem Core-Ausbau.
        services.toast(`Vier-Augen-Antrag gestellt: ${request.label}`, 'info');
        return { requested: true };
      },
    },
    telemetry: {
      trackEvent: (name, props) => console.info('[telemetry]', name, props ?? {}),
      trackError: (error) => console.error('[telemetry]', error),
    },
  };

  return {
    ctx,
    notifyNavigate: (subpath) => navListeners.forEach((cb) => cb(subpath)),
  };
}
